import { Component, EventEmitter, HostListener, Inject, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { CellEditingStartedEvent, CellEditingStoppedEvent, GridOptions, IDetailCellRendererParams, IRichCellEditorParams, IRowNode, RowNode, ITooltipParams, RowDataUpdatedEvent, GridApi } from "ag-grid-community";
import { cloneDeep, uniqBy, uniq } from 'lodash';
import { CxoneOrderManagementGridProductComponent } from "../cxone-custom-grid-components/cxone-order-management-grid-product/cxone-order-management-grid-product.component";
import { MarketplaceOrderStatus } from "src/app/gamification/command-center/models/marketplace-order.model";
import { DecimalPipe, formatDate } from "@angular/common";
import { CxoneOrderManagementGridLoaderComponent } from "../cxone-custom-grid-components/cxone-order-management-grid-loader/cxone-order-management-grid-loader.component";
import { CxoneOrderManagementGridTextContainerComponent } from "../cxone-custom-grid-components/cxone-order-management-grid-text-container/cxone-order-management-grid-text-container.component";
import { CxoneOrderManagementGridNotesComponent } from "../cxone-custom-grid-components/cxone-order-management-grid-notes/cxone-order-management-grid-notes.component";
import { PointStoreProductOrderService } from "src/app/gamification/marketplace/agent-side/services/point-store-product-order.service";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { CxoneOrderManagementService } from "../../services/cxone-order-management.service";
import { StatusCellEditRenderer } from "../cxone-custom-grid-components/cxone-order-management-grid-status/cxone-order-management-grid-status.component";
import { CvCustomDialogService } from "src/app/reusables/custom-dialog/cv-custom-dialog.service";
import { NgbModalOptions, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { StatusCellRenderer } from "../cxone-custom-grid-components/cxone-order-management-grid-status-cell/cxone-order-management-grid-status-cell.component";
import { CxoneOrderManagementNotesTooltipComponent } from "../cxone-custom-grid-components/cxone-order-management-notes-tooltip/cxone-order-management-notes-tooltip.component";
import { AgGridAngular } from "ag-grid-angular";
import { CxoneOrderManagementHeaderComponent } from "../cxone-custom-grid-components/cxone-order-management-header/cxone-order-management-header.component";
import { ChangeDetectorRef } from '@angular/core';

import moment from 'moment';

@Component({
  selector: 'cxone-order-management-grid',
  templateUrl: './cxone-order-management-grid.component.html',
  styleUrl: './cxone-order-management-grid.component.scss'
})
export class CxoneOrderManagementGridComponent implements OnInit, OnChanges, OnDestroy {
    
  orders = null;
  pspos = null;
  public localizationStrings: Map<string, string> = new Map<string, string>();
  tableLocalization = new Map();
  availableStatuses = [
    MarketplaceOrderStatus.Processing,
    MarketplaceOrderStatus.InTransit,
    MarketplaceOrderStatus.Shipped,
    MarketplaceOrderStatus.ReadyForPickup,
    MarketplaceOrderStatus.Complete,
    MarketplaceOrderStatus.Cancelled
  ];
  childrensToChangeHeight: number = 1;
  rowHeightChildDetailRow: number = 136;
  originalOrder: any = null;
  baseHeightDetailRow: number = 182;
  defaultRowHeight: number = 300;
  detailRowHeaderHeight: number = 38;
  orderManagementGridOptions: GridOptions = null;
  DateFormat = "MMM d, y";
  isOrderNumberClicked: boolean = false;
  orderNumberPreviousSort: number = 0; //0 => neutral, 1  =>asc, 2 => desc
  isStatusDropdownOpen: boolean = false;
  innertableApiGrid: GridApi = null;
  refreshDynamicProductCellWidth: number;
  refreshDynamicVariantCellWidth: number;
  private onWheelDetected: (e: any) => void;

  @Input() ordersList: any[] = [];
  @Input() status: any[];
  @Input() timeInterval: number;
  @Input() search: string;
  @Input() ordersLoading: boolean;
  @Input() selectGroupChange: boolean;
  @Input() resetFilter: boolean;
  @Input() groupSelectorEmtpy: boolean;
  @Output() groupSelectorData: EventEmitter<Map<string, { newValue: any[], oldValue: any[] }>> = new EventEmitter();

  @Output() countOrders: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('cxoneOrderManagementGrid') agGrid: AgGridAngular;
  
  @HostListener('click', ['$event']) onClickComponent(e) {
    if (e.target) {
      const target = e.target.closest('.mot-order-id');
      if (target) {
        this.isOrderNumberClicked = true;
      } else {
        this.isOrderNumberClicked = false;
      }
    }
  }

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private decimalPipe: DecimalPipe,
    private pointStoreProductOrderService: PointStoreProductOrderService,
    private cvLocalizationService: CVLocalizationService,
    private orderManagementService: CxoneOrderManagementService,
    private dialogService: CvCustomDialogService,
    private cdref: ChangeDetectorRef
  ) {
    //Added this listener to stop editing when the innertable's status dropdown closes
    this.onWheelDetected = (e) => {
        const dropdownList = document.getElementsByClassName("ag-rich-select-virtual-list-viewport");
        if(!document.body.contains(dropdownList[0]))
            this.innertableApiGrid?.stopEditing();
    }
    window.addEventListener('wheel', this.onWheelDetected);
  }

  async ngOnInit() {
    this.pspos = this.pointStoreProductOrderService.pointStoreProductOrderObj;
    await this.loadLocalization();
  }

  ngOnDestroy() {
    window.removeEventListener('wheel', this.onWheelDetected);
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  formatOrders(orders: any[]) {
    const ordersClone: any[] = cloneDeep(orders ?? []);
    this.orders = ordersClone.reverse();
    return this.orders;
  }

  async loadLocalization() {
    this.localizationStrings = await this.cvLocalizationService.GetSection("Order_Management");
    this.tableLocalization = await this.cvLocalizationService.GetSection("AgGrid");
    await this.orderManagementService.getLocalizationStrings()
    this.setupGrid();
  }

  getLocalizationString(key: string, defaultValue: string): string {
    return this.localizationStrings.has(key) ? this.localizationStrings.get(key) : defaultValue;
  }

  setupGrid(): void {
    this.orderManagementGridOptions = {
      masterDetail: true,
      suppressDragLeaveHidesColumns: true,
      suppressRowGroupHidesColumns: true,
      suppressContextMenu: true,
      animateRows: true,
      suppressCellFocus: true,
      keepDetailRowsCount: 1,
      alwaysShowVerticalScroll: false,
      singleClickEdit: true,
      stopEditingWhenCellsLoseFocus: true,
      rowHeight: 44,
      scrollbarWidth: 8,
      suppressPreventDefaultOnMouseWheel: true,
      loadingOverlayComponent: CxoneOrderManagementGridLoaderComponent,
      enableBrowserTooltips: false,
      suppressRowTransform: true,
      onGridReady: params => {
        this.orderManagementService.gridApi = params;
        params.api.sizeColumnsToFit();
        if (this.ordersList.length > 0) {
          this.agGrid?.api?.setGridOption('rowData', this.formatOrders(this.ordersList));
        }
      },
      onColumnResized: event => {
        if (event.columns.length > 1) {
            event.api.sizeColumnsToFit();
        }
      },
      overlayNoRowsTemplate: `<div class="error-container">
                                <svg class="cxoneFavoriteState mb-2" style="width: 165.94px !important;height:92px !important;">
                                    <use href="../App_Themes/CXone/icons/empty-state.svg#empty-state"></use>
                                </svg>
                                <p class="cxone-error-text" style="color:#758994;">${this.getLocalizationString("No_Orders_Found", "No orders found")}</p>
                              </div>`,
      getRowId: ({ data: order }) => String(order.PointStoreOrderID),
      columnDefs: [
        {
          field: "arrow",
          cellRenderer: "agGroupCellRenderer",
          headerName: "",
          resizable: false,
          maxWidth: 40,
          lockPosition: "left",
          suppressMenu: true,
          suppressFiltersToolPanel: true,
          suppressColumnsToolPanel: true,
          tooltipField: 'notes',
        },
        {
          field: "PointStoreOrderID",
          headerClass: 'mot-order-id',
          minWidth: 100,
          headerName: this.getLocalizationString("Order_Number", "Order Number"),
          sortable: true,
          resizable: true,
          valueGetter: (params) => params.data.PointStoreOrderID,
          cellRendererSelector: (params) => {
            return {
              component: CxoneOrderManagementGridTextContainerComponent,
              params: {
                text: params.data.PointStoreOrderID
              }
            }
          },
          lockVisible: true,
        },
        {
          field: "OrderStatus",
          minWidth: 100,
          cellStyle: { marginLeft: '15px' },
          headerName: this.getLocalizationString("Order_Status", "Order Status"),
          sortable: true,
          resizable: true,
          editable: (params) => params.data.OrderStatus !== MarketplaceOrderStatus.Complete && params.data.OrderStatus !== MarketplaceOrderStatus.Cancelled,
          valueFormatter: (params) => this.orderManagementService.getStatusName(params.value as MarketplaceOrderStatus),
          cellRendererSelector: (params) => {
            return {
              component: StatusCellRenderer,
              params: {
                disable: params.data.OrderStatus === MarketplaceOrderStatus.Complete || params.data.OrderStatus === MarketplaceOrderStatus.Cancelled,
                text: this.orderManagementService.getStatusName(params.data.OrderStatus),
              }
            }
          },
          cellEditor: "agRichSelectCellEditor",
          cellEditorParams: (params: any) => ({
            values: this.availableStatuses,
            cellRenderer: StatusCellEditRenderer,
            valueListGap: 0,
            cellHeight: 30,
          }) as IRichCellEditorParams,
          comparator: (valueA, valueB) => {
            const statusA = this.orderManagementService.getStatusName(valueA as MarketplaceOrderStatus);
            const statusB = this.orderManagementService.getStatusName(valueB as MarketplaceOrderStatus);

            if (statusA == statusB) return 0;
            return (statusA > statusB) ? 1 : -1;
          },
          cellClass: "status-cell",
        },
        {
          field: "DateOrdered",
          minWidth: 100,
          headerName: this.getLocalizationString("Order_Date", "Order Date"),
          sortable: true,
          resizable: true,
          valueGetter: (params) => params.data.DateOrdered,
          valueFormatter: (params) => formatDate(params.value, this.DateFormat, this.locale),
          cellRendererSelector: (params) => {
            return {
              component: CxoneOrderManagementGridTextContainerComponent,
              params: {
                text: formatDate(params.value, this.DateFormat, this.locale)
              }
            }
          },
        },
        {
          field: "LoginName",
          minWidth: 100,
          headerName: this.getLocalizationString("Ordered_By", "Ordered By"),
          sortable: true,
          resizable: true,
          valueGetter: (params) => params.data.LoginName,
          cellRendererSelector: (params) => {
            return {
              component: CxoneOrderManagementGridTextContainerComponent,
              params: {
                text: params.data.LoginName
              }
            }
          },
          comparator: this.stringComparator
        },
        {
          field: "LoginID",
          minWidth: 100,
          headerName: this.getLocalizationString("Login_ID", "Agent ID"),
          sortable: true,
          resizable: true,
          valueGetter: (params) => params.data.LoginID,
          cellRendererSelector: (params) => {
            return {
              component: CxoneOrderManagementGridTextContainerComponent,
              params: {
                text: params.data.LoginID
              }
            }
          },
        },
        {
          field: "RelatedParentGroupName",
          minWidth: 100,
          headerName: this.getLocalizationString("Team", "Team"),
          sortable: true,
          resizable: true,
          valueGetter: (params) => params.data.RelatedParentGroupName,
          cellRendererSelector: (params) => {
            return {
              component: CxoneOrderManagementGridTextContainerComponent,
              params: {
                text: params.data.RelatedParentGroupName
              }
            }
          },
          comparator: this.stringComparator
        },
        {
          field: "OrderTotal",
          minWidth: 100,
          headerClass: "price-alignment",
          headerName: this.getLocalizationString("Total_Price", "Total Price"),
          sortable: true,
          resizable: true,
          valueGetter: (params) => params.data.OrderTotal,
          cellRendererSelector: (params) => {
            return {
              component: CxoneOrderManagementGridTextContainerComponent,
              params: {
                text: this.decimalPipe.transform(parseFloat(params.data.OrderTotal), undefined, CVCultureName),
                customStyle: "padding-right: 25px; padding-left: 12px; text-align: right; justify-content: flex-end;",
              }
            }
          },
          comparator: (valueA, valueB) => {
            if (valueA == valueB) return 0;
            return (valueA > valueB) ? 1 : -1;
          }
        },
        {
          field: "Note",
          tooltipValueGetter: (p: ITooltipParams) => {
            if (p.data.Note === null || p.data.Note === "" || p.data.Note === undefined) {
              return;
            } else {
              return p.data.Note;
            }
          },
          minWidth: 100,
          sortable: false,
          resizable: true,
          headerName: this.getLocalizationString("Notes", "Notes"),
          valueGetter: (p => p.data.Note),
          cellRendererSelector: (params) => {
            return {
              component: CxoneOrderManagementGridNotesComponent,
              params: {
                notes: params.data.Note,
                order: params.data,
                gridApi: this.orderManagementGridOptions,
              }
            }
          },
          comparator(valueA, valueB, nodeA, nodeB, isDescending) {
            const a = valueA === '' ? valueA : valueA.toString().trim().toLowerCase();
            const b = valueB === '' ? valueB : valueB.toString().trim().toLowerCase();
            if (a === b) return 0;
            return (a > b) ? 1 : -1;
        },
        
        },
      ],
      components: {
        agColumnHeader: CxoneOrderManagementHeaderComponent,
      },
      onSortChanged: (event) => this.onSortChangedEvent(event),
      defaultColDef: {
        flex: 1,
        tooltipComponent: CxoneOrderManagementNotesTooltipComponent,
      },
      rowData: [],
      localeText: Object.fromEntries(this.tableLocalization.entries()),
      detailCellRendererParams: {
        detailGridOptions: {
          suppressDragLeaveHidesColumns: true,
          suppressRowGroupHidesColumns: true,
          suppressContextMenu: true,
          detailRowAutoHeight: true,
          alwaysShowVerticalScroll: false,
          headerHeight: 36,
          rowHeight: this.rowHeightChildDetailRow,
          suppressPreventDefaultOnMouseWheel: true,
          paginationAutoPageSize: false,
          singleClickEdit: true,
          scrollbarWidth: 0,
          columnDefs: [
            {
              field: "spacer",
              headerName: "",
              resizable: false,
              maxWidth: 40,
              lockPosition: "left",
              suppressMenu: true,
              tooltipField: 'product',
            },
            {
              field: "product",
              headerName: this.getLocalizationString("Product", "Product"),
              rowDrag: false,
              sortable: true,
              resizable: true,
              minWidth: 150,
              cellStyle: {  maxWidth: this.refreshDynamicProductCellWidth + 'px' },
              flex: 3,
              cellRendererSelector: (params) => {
                return {
                  component: CxoneOrderManagementGridProductComponent,
                  params: {
                    title: params.data.product,
                    imageURL: `../Admin/ImageResizer.aspx?imgid=${params.data.productImageID}&w=auto&h=190`,
                    imageID: params.data.productImageID
                  }
                }
              },
              comparator: this.stringComparator
            },
            {
              field: "variant",
              headerName: this.getLocalizationString("Variant", "Variant"),
              rowDrag: false,
              flex: 1,
              sortable: true,
              resizable: true,
              minWidth: 100,
              cellStyle: {  maxWidth: this.refreshDynamicVariantCellWidth + 'px' },
              cellRendererSelector: (params) => {
                return {
                  component: CxoneOrderManagementGridTextContainerComponent,
                  params: {
                    text: params.value
                  }
                }
              },
              comparator: this.stringComparator
            },
            {
              field: "status",
              cellStyle: { marginLeft: '15px' },
              headerName: this.getLocalizationString("Status", "Status"),
              rowDrag: false,
              editable: (params) => params.data.status !== MarketplaceOrderStatus.Complete && params.data.status !== MarketplaceOrderStatus.Cancelled,
              maxWidth: 200,
              flex: 2,
              sortable: true,
              resizable: true,
              minWidth: 100,
              cellRenderer: StatusCellRenderer,
              cellRendererSelector: (params) => {
                return {
                  component: StatusCellRenderer,
                  params: {
                    disable: params.data.status === MarketplaceOrderStatus.Complete || params.data.status === MarketplaceOrderStatus.Cancelled,
                    text: this.orderManagementService.getStatusName(params.data.status),
                  }
                }
              },
              cellEditor: "agRichSelectCellEditor",
              cellEditorParams: (params: any) => ({
                values: this.availableStatuses,
                cellRenderer: StatusCellEditRenderer,
                valueListGap: 0,
                cellHeight: 30,
              }) as IRichCellEditorParams,
              comparator: (valueA, valueB) => {
                const statusA = this.orderManagementService.getStatusName(valueA as MarketplaceOrderStatus);
                const statusB = this.orderManagementService.getStatusName(valueB as MarketplaceOrderStatus);

                if (statusA == statusB) return 0;
                return (statusA > statusB) ? 1 : -1;
              },
              valueFormatter: (params) => this.orderManagementService.getStatusName(params.value),
              cellClass: "status-cell",
              onCellClicked: (event) => {
                this.innertableApiGrid = event.api;
                if (!this.isStatusDropdownOpen)
                  event.api.stopEditing();

                this.isStatusDropdownOpen = false;
              }
            },
            {
              field: "quantity",
              headerName: this.getLocalizationString("Quantity", "Quantity"),
              rowDrag: false,
              flex: 1,
              sortable: true,
              resizable: true,
              minWidth: 100,
              cellRendererSelector: (params) => {
                return {
                  component: CxoneOrderManagementGridTextContainerComponent,
                  params: {
                    text: this.decimalPipe.transform(params.value, undefined, CVCultureName)
                  }
                }
              }
            },
            {
              field: "price",
              headerName: this.getLocalizationString("Price", "Price"),
              rowDrag: false,
              flex: 1,
              sortable: true,
              resizable: true,
              minWidth: 100,
              headerClass: "price-alignment",
              cellClass: "price-alignment",
              cellRendererSelector: (params) => {
                return {
                  component: CxoneOrderManagementGridTextContainerComponent,
                  params: {
                    text: this.decimalPipe.transform(parseFloat(params.value), undefined, CVCultureName),
                    customStyle: "padding-right: 25px; padding-left: 12px; text-align: right; justify-content: flex-end;",
                  }
                }
              }
            }
          ],
        onCellEditingStarted: (params: CellEditingStartedEvent) => {
            let productOrdered = params.data;
            let order = this.agGrid?.api?.getRowNode(String(productOrdered.PointStoreOrderID));
            if (!order) {
                return;
            }
            this.originalOrder = angular.copy(order.data);

            if (params.column.getColId() === 'status') {
                const rowNode: IRowNode = this.agGrid?.api?.getRowNode(params.data.PointStoreOrderID);
                const { rowDetailNode, childrenLength } = this.getRowDetailNode(rowNode);
                this.isStatusDropdownOpen = true;
                if (this.validateChildrensToChangeHeight(childrenLength)) {
                    rowDetailNode.setRowHeight(this.defaultRowHeight);
                    this.agGrid?.api?.onRowHeightChanged();
                }
            }
        },
        onCellEditingStopped: (params) => {
            const pointStoreOrderId = params.data.PointStoreOrderID;

            if (params.column.getColId() === 'status') {
                this.agGrid?.api?.stopEditing(true);
                params.api.hidePopupMenu();

                const rowNode: IRowNode = this.agGrid?.api?.getRowNode(pointStoreOrderId);
                const { rowDetailNode, childrenLength } = this.getRowDetailNode(rowNode);

                if (this.validateChildrensToChangeHeight(childrenLength)) {
                    rowDetailNode.setRowHeight(this.calculateDynamicHeight(childrenLength));
                    this.agGrid?.api?.onRowHeightChanged();
                }
            }

            //Warning if the user change to Completed or Cancelled
            if (
                params.data.status === MarketplaceOrderStatus.Complete ||
                params.data.status === MarketplaceOrderStatus.Cancelled
            ) {
                const modalOptions: NgbModalOptions = {
                    keyboard: false,
                    backdrop: "static",
                    centered: true,
                };
                const modalTitle = this.cvLocalizationService.getCachedText(
                    "Order_Management",
                    "Change_Status",
                    "Change status"
                );
                const prompt = `<p class='modal-body-text pb-2'>${this.cvLocalizationService.getCachedText(
                    "Order_Management",
                    "Change_Status_Warning",
                    "You cannot undo a status change to"
                )} ${this.cvLocalizationService.getCachedText(
                    "Order_Management",
                    params.data.status === MarketplaceOrderStatus.Complete
                        ? "Completed"
                        : "Cancelled",
                    params.data.status === MarketplaceOrderStatus.Complete
                        ? "Completed"
                        : "Cancelled"
                )}.</p>`;
                const okLabel = this.cvLocalizationService.getCachedText(
                    "Order_Management",
                    "Modal_Ok",
                    "Change"
                );
                const cancelLabel = this.cvLocalizationService.getCachedText(
                    "Common",
                    "Cancel_Button",
                    "Cancel"
                );
                const modal: NgbModalRef = this.dialogService.showDialog(
                    modalTitle,
                    prompt,
                    4,
                    cancelLabel,
                    okLabel,
                    true,
                    () => this.changeProductStatus(params),
                    () => {},
                    "change-status-dialog",
                    true,
                    modalOptions
                );
                modal.dismissed.subscribe(() => {
                    const rowNode = this.agGrid?.api?.getRowNode(String(this.originalOrder.PointStoreOrderID));
                    if (rowNode) {
                        this.agGrid?.api?.applyTransaction({
                            update: [this.originalOrder],
                        });
                    }

                    this.originalOrder = null;
                });
            } else {
                this.changeProductStatus(params);
              const columnStateData = params.columnApi.getColumnState();
              columnStateData.forEach(e => {
                if (e.colId === "product") {
                  this.refreshDynamicProductCellWidth = e.width;
                }
                
                if (e.colId === "variant") {
                  this.refreshDynamicVariantCellWidth = e.width;
                }
              });
            }
            
        },
        
          defaultColDef: {
            flex: 1,
            tooltipComponent: CxoneOrderManagementNotesTooltipComponent,
            suppressMenu: true,
            cellStyle: (params) => ({
                display: "flex",
                alignItems: "center"
            }) as any,
          },
        },
        getDetailRowData: (params) => {
          const detailRowData = params.data.ProductsCollection.map(({ _PointStoreProductOrderedDTO: product }) => {
            return {
              product: product.Title,
              variant: product.VariationName ?? "NA",
              status: product.ProductStatus,
              quantity: product.ProductQuantity,
              price: product.ProductCost,
              productImageID: product.ProductImageID,
              PointStoreOrderID: product.PointStoreOrderID,
              VariationId: product.Variation,
              productId: product.PointStoreProductID,
            }
          });
          params.successCallback(detailRowData);
        },
      } as IDetailCellRendererParams<any, any>,
      onCellEditingStarted: (params) => {
        let productOrdered = params.data;
        let order = this.agGrid?.api?.getRowNode(String(productOrdered.PointStoreOrderID));
        if (!order) {
            return;
        }
        this.originalOrder = angular.copy(order.data);
      },
      onCellEditingStopped: async (e) => {
        // check for order status changes
        if (
            e.column.getColId() === "OrderStatus" &&
            e.data.OrderStatus !== this.originalOrder.OrderStatus
        ) {
            //Warning if the user change to Completed or Cancelled
            if (
                e.data.OrderStatus === MarketplaceOrderStatus.Complete ||
                e.data.OrderStatus === MarketplaceOrderStatus.Cancelled
            ) {
                const modalOptions: NgbModalOptions = {
                    keyboard: false,
                    backdrop: "static",
                    centered: true,
                };
                const modalTitle = this.cvLocalizationService.getCachedText(
                    "Order_Management",
                    "Change_Status",
                    "Change status"
                );
                const prompt = `<p class='modal-body-text pb-2'>${this.cvLocalizationService.getCachedText(
                    "Order_Management",
                    "Change_Status_Warning",
                    "You cannot undo a status change to"
                )} ${this.cvLocalizationService.getCachedText(
                    "Order_Management",
                    e.data.OrderStatus === MarketplaceOrderStatus.Complete
                        ? "Completed"
                        : "Cancelled",
                    e.data.OrderStatus === MarketplaceOrderStatus.Complete
                        ? "Completed"
                        : "Cancelled"
                )}.</p>`;
                const okLabel = this.cvLocalizationService.getCachedText(
                    "Order_Management",
                    "Modal_Ok",
                    "Change"
                );
                const cancelLabel = this.cvLocalizationService.getCachedText(
                    "Common",
                    "Cancel_Button",
                    "Cancel"
                );
                const modal: NgbModalRef = this.dialogService.showDialog(
                    modalTitle,
                    prompt,
                    4,
                    cancelLabel,
                    okLabel,
                    true,
                    () => this.changeOrderStatus(e),
                    () => {},
                    "change-status-dialog",
                    true,
                    modalOptions
                );
                modal.dismissed.subscribe(() => {
                    const rowNode = this.agGrid?.api?.getRowNode(String(this.originalOrder.PointStoreOrderID));
                    if (rowNode) {
                        this.agGrid?.api?.applyTransaction({
                            update: [this.originalOrder],
                        });
                    }

                    this.originalOrder = null;
                });
            } else {
                this.changeOrderStatus(e);
            }
        }
      },
      onFirstDataRendered: (e) => {
        this.getFilteredRows();
      },
      onRowDataUpdated: (event: RowDataUpdatedEvent) => {
        this.setCountLabel(event);
      },
      onFilterChanged: (event) => {
        this.manageNoRowsOverlay();
        requestAnimationFrame(() =>
          this.agGrid?.api?.redrawRows()
        );
        this.setCountLabel(event);
      },
      isExternalFilterPresent: () => true,
      doesExternalFilterPass: (node) => {
        let item = node.data;
        let match_date = false;
        let match_status = false;
 
        let status: any[] = this.pspos.pointStoreProductAdminOrderFilter.SelectedStatuses;
        const search = this.pspos.pointStoreProductAdminOrderFilter.searchOrder;
 
        if (!status) {
          //default status
          status = [];
        }
 
        match_status = status.length === 0 || status.some((status: string) => status === item.OrderStatus);
 
        //node.data
        const position = this.pointStoreProductOrderService.getDatePosition();
        if (position == 0 || position == 1) {
          //30 or 60 days
          match_date = this.daysBetweenDates(this.pointStoreProductOrderService.pointStoreProductOrderObj.pointStoreProductAdminOrderFilter.date[position].value, item.DateOrdered);
        } else if (position == 2) {
          //6 months
          match_date = this.daysBetweenDates(this.pointStoreProductOrderService.pointStoreProductOrderObj.pointStoreProductAdminOrderFilter.date[position].value, item.DateOrdered);
        } else if (position >= 3) {
          match_date = this.daysBetweenDates(this.pointStoreProductOrderService.pointStoreProductOrderObj.pointStoreProductAdminOrderFilter.date[position].value, item.DateOrdered, true);
        } else if (position == -1) {
          match_date = true;
        }
 
        const match_emp_group = this.pointStoreProductOrderService.setFilterMatchesEmpGroupCXOne(item);

        if (match_date && match_status && match_emp_group) {
          // go through each column value and return as soon as we get a match
          const filterValueLower = search.toLowerCase();
 
          //-- Empty/Whitespace Input --
          if (filterValueLower.trim() == '')
            return true;
 
          //search on every column by the default or the formatted value 
          return this.orderManagementGridOptions.columnDefs.some((colDef:any) => {
            const formattedValue = colDef.valueFormatter ? colDef.valueFormatter({ value: node.data[colDef.field] }) : node.data[colDef.field];
            return `${formattedValue}`.toLowerCase().includes(filterValueLower);
          })
        }
        return false;
      },
    }
  }

  stringComparator(valueA: string, valueB: string): number {
    const lowerCaseComparison = valueA.toLowerCase().localeCompare(valueB.toLowerCase());
    if (lowerCaseComparison !== 0) return lowerCaseComparison;
    return valueA.localeCompare(valueB);
  }

  /**
     * 
     * @param datePosition 
     * @param dateOrdered 
     * @param isYear 
     * @returns 
     */
  daysBetweenDates(datePosition: number, dateOrdered: any, isYear: boolean = false): boolean {
    const startDate = moment().subtract(datePosition, 'days');
    const currentDate = moment();
    const orderDate = moment(dateOrdered);
    let res = false;
    if (isYear) {
      res = orderDate.year() === datePosition;
    } else {
      res = orderDate.isBetween(startDate, currentDate, 'days', '[]')
    }
    return res;
  }

  getFilteredRows() {
    this.pspos.dataFiltered = [];
    this.agGrid?.api?.forEachNodeAfterFilter((node: RowNode) => {
      this.pspos.dataFiltered.push(node.data);
    });
    setTimeout(() => {
      this.pspos.pointStoreProductAdminOrderFilter.groupSelectorChanged = false;
      this.pspos.pointStoreProductAdminOrderFilter.resetFilter = false;
      this.pspos.pointStoreProductAdminOrderFilter.groupSelectorEmtpy = false;
    });
  }

  applyFilter() {
    this.agGrid?.api?.onFilterChanged();
    this.getFilteredRows();
  }

  setCountLabel(event: any) {
    let count = 0;
    event.api.forEachNodeAfterFilter(() => {
      count++;
    });
    this.countOrders.emit(count);
  }

  manageNoRowsOverlay() {
    if (this.agGrid?.api?.getDisplayedRowCount() === 0) {
      this.agGrid?.api?.showNoRowsOverlay();
    }
    else {
      this.agGrid?.api?.hideOverlay();
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if(!this.agGrid?.api) {
      return;
    }

    if(changes.ordersLoading && changes.ordersLoading.currentValue) {
      this.agGrid?.api?.showLoadingOverlay();
      return;
    }

    if (changes.ordersList) {
      this.agGrid?.api?.setGridOption('rowData', this.formatOrders(changes.ordersList.currentValue));
      this.refreshRowData();
      if(changes.ordersList.currentValue) {
        this.populateStatusTeamAgentFromTable();
      }
    }

    if(changes.timeInterval || changes.resetFilter || changes.status?.currentValue?.length === 0 || changes?.groupSelectorEmtpy?.currentValue) {
      this.refreshRowData();
      this.populateStatusTeamAgentFromTable();
    }

    if (changes.search || changes.status || changes.selectGroupChange) {
      this.refreshRowData();
    }
  }
  
  refreshRowData() {
    this.applyFilter();
    this.manageNoRowsOverlay();
    this.agGrid?.api?.refreshCells();
  }
  
  getRowDetailNode(rowNode: IRowNode) {
    if (rowNode instanceof RowNode) {
        const rowDetailNode: RowNode = rowNode.detailNode;
        const childrenLength = rowDetailNode.data.ProductsCollection?.length;
        return {
            rowDetailNode, childrenLength
        };
    }
  }

  validateChildrensToChangeHeight(childrenLength): boolean {
    return childrenLength <= this.childrensToChangeHeight;
  }

  calculateDynamicHeight(childrenLength): number {
    return (childrenLength * this.rowHeightChildDetailRow) + this.detailRowHeaderHeight;
  }

  async changeOrderStatus(e: CellEditingStoppedEvent<any, any>) {
    const { ProductsCollection, OrderStatus } = e.data;
    const { Cancelled, Complete } = MarketplaceOrderStatus;

    // update all products to this status
    ProductsCollection.forEach((product) => {
        const { ProductStatus } = product._PointStoreProductOrderedDTO;

        if (ProductStatus === Cancelled || ProductStatus === Complete) {
            return;
        }

        product._PointStoreProductOrderedDTO.ProductStatus = OrderStatus;
        product._Add = false;
        product._Dirty = true; //means object was modified
    });

    // get lowest product status (sorted by the order in availableStatuses)
    const ordersSortedByStatus = e.data.ProductsCollection.map(
        (p) => p._PointStoreProductOrderedDTO.ProductStatus
    ).sort(
        (a, b) =>
            this.availableStatuses.indexOf(a) -
            this.availableStatuses.indexOf(b)
    );

    e.data.OrderStatus = ordersSortedByStatus[0];

    // update on grid
    e.api.applyTransaction({ update: [e.data] });

    // update on server
    await this.orderManagementService.onOrderUpdated({
        newValue: e.data,
        oldValue: this.originalOrder,
    });

    this.populateStatusTeamAgentFromTable();

    this.originalOrder = null;
  }

  changeProductStatus(params: CellEditingStoppedEvent<any, any>) {
    const { PointStoreOrderID, productId, VariationId, status } = params.data;

    if (PointStoreOrderID !== this.originalOrder?.PointStoreOrderID) {
        return;
    }

    // find the order related to this product
    
    const node = this.agGrid?.api?.getRowNode(String(PointStoreOrderID));

    if (!node) {
        return;
    }
    const order = node.data;
    
    // find the product in the order
    const findProductInOrder = (product) =>
        product._PointStoreProductOrderedDTO.PointStoreProductID ===
            productId &&
        product._PointStoreProductOrderedDTO.Variation === VariationId;

    const updatedProd = order.ProductsCollection.find(findProductInOrder);

    updatedProd._Add = false;
    updatedProd._Dirty = true; //means object was modified
    updatedProd._PointStoreProductOrderedDTO.ProductStatus = status;

    // get lowest product status (sorted by the order in availableStatuses)
    const ordersSortedByStatus = order.ProductsCollection.map(
        (p) => p._PointStoreProductOrderedDTO.ProductStatus
    ).sort(
        (a, b) =>
            this.availableStatuses.indexOf(a) -
            this.availableStatuses.indexOf(b)
    );

    order.OrderStatus = ordersSortedByStatus[0];

    // check for changes
    const originalProductOrdered =
        this.originalOrder.ProductsCollection.find(findProductInOrder);

    if (
        order.OrderStatus === this.originalOrder.OrderStatus &&
        originalProductOrdered._PointStoreProductOrderedDTO.ProductStatus ===
            status
    ) {
        // no changes, just exit
        this.originalOrder = null;
        return;
    }

    // update on grid
    this.agGrid?.api?.applyTransaction({ update: [order] });

    // update on server
    this.orderManagementService.onOrderUpdated({
        newValue: order,
        oldValue: this.originalOrder,
    });

    this.populateStatusTeamAgentFromTable();

    this.originalOrder = null;
  }

  // dynamically assigning detail row height 
  getRowHeight(params) {
    const isDetailRow = params.node.detail;
    const rowDetailHeight = 136; //pixels
    const headerEstimated = 38; // pixels
    // for all rows that are not detail rows, return nothing
    if (!isDetailRow) { return undefined; }

    // otherwise return height based on number of rows in detail grid
    return (params.data.ProductsCollection.length * rowDetailHeight) + headerEstimated;
  };

  onSortChangedEvent(event): void {
    const sortedColumn = event.columnApi.getColumnState().find(col => Boolean(col.sort));
    if (sortedColumn == null || sortedColumn == undefined) {
      if (!this.isOrderNumberClicked) {
        this.agGrid?.columnApi.applyColumnState({ state: [{ colId: 'PointStoreOrderID', sort: 'desc' }] });
        this.orderNumberPreviousSort == 2;
      } else {
        if (this.orderNumberPreviousSort == 0) {
          this.agGrid?.columnApi.applyColumnState({ state: [{ colId: 'PointStoreOrderID', sort: 'desc' }] });
          this.orderNumberPreviousSort = 2;
        } else {
          if (this.orderNumberPreviousSort == 2) {
            this.agGrid?.columnApi.applyColumnState({ state: [{ colId: 'PointStoreOrderID', sort: 'asc' }] });
            this.orderNumberPreviousSort = 1;
          } else {
            this.agGrid?.columnApi.applyColumnState({ state: [{ colId: 'PointStoreOrderID', sort: 'desc' }] });
            this.orderNumberPreviousSort = 2;
          }
        }
      }
    } else {
      if (!this.isOrderNumberClicked) {
        this.orderNumberPreviousSort = 2;
      } else {
        if (sortedColumn.sort == 'asc') {
          this.orderNumberPreviousSort = 2;
        }
      }
    }

    if (sortedColumn != undefined) {
      this.pspos.sortAdminBy = sortedColumn.colId;
      this.pspos.sortAdminReverse = (sortedColumn.sort == "asc") ? false : true;
      this.refreshRowData();
    }
  }

  populateStatusTeamAgentFromTable() {
    let status = [];
    let teams = [];
    let agents = [];
    let groupSelectorData: Map<string, { newValue: any[], oldValue: any[] }> = new Map();

    this.pspos.dataFiltered?.forEach(order => {
      status.push(order.OrderStatus);
      teams.push({id: order.Group2ID, name: order.RelatedParentGroupName });
      agents.push({id: order.Group1ID, name: order.LoginName, loginId: order.LoginID, parent: order.Group2ID  });
    });

    const teamLst = uniqBy(teams, 'id');
    const agentLst = uniqBy(agents, 'loginId');
    const statusLst = uniq(status);

    const statusList = this.pspos.OrderStatus.filter(s => statusLst.includes(s.value));

    groupSelectorData.set('teams', { newValue: teamLst, oldValue: teamLst });
    groupSelectorData.set('agents', { newValue: agentLst, oldValue: agentLst });
    groupSelectorData.set('status', { newValue: statusList, oldValue: statusList });

    this.groupSelectorData.emit(groupSelectorData);
  }
}
